import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { prefs } from "../../lib/prefs";

/**
 * Shared primitives, matched to the v2 prototype.
 *
 * Hand-rolled rather than pulled from a component library: the set needed is
 * small, the look is specific, and for a proof of concept the extra dependency
 * costs more than these few hundred lines.
 */

// ---------------------------------------------------------------- status
const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  PUBLISHED: { bg: "#E6F7DC", fg: "#2F6420" },
  AWAITING_REVIEW: { bg: "#FCF6D2", fg: "#75650B" },
  AWAITING_APPROVAL: { bg: "#F3EEFA", fg: "#4B2E83" },
  WORK_IN_PROGRESS: { bg: "#EFECEE", fg: "#57515B" },
  CHANGES_REQUESTED: { bg: "#FFECEC", fg: "#B23438" },
  QUEUED_FOR_PUBLISH: { bg: "#E4F5FB", fg: "#1D5F72" },
};

export function StatusPill({
  status,
  label,
  wide,
}: {
  status: string;
  label: string;
  wide?: boolean;
}) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.WORK_IN_PROGRESS;
  return (
    <span
      className="pill"
      style={{ background: style.bg, color: style.fg, paddingInline: wide ? 9 : 8 }}
    >
      {label}
    </span>
  );
}

const ROLE_STYLE: Record<string, { bg: string; fg: string }> = {
  ADMIN: { bg: "#E4F5FB", fg: "#1D5F72" },
  NCM: { bg: "#E6F7DC", fg: "#2F6420" },
  NCE: { bg: "#F3EEFA", fg: "#4B2E83" },
  SYSTEM_ENGINEER: { bg: "#EFECEE", fg: "#57515B" },
};

export function RolePill({ role, label }: { role: string; label: string }) {
  const style = ROLE_STYLE[role] ?? ROLE_STYLE.SYSTEM_ENGINEER;
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-pill px-[7px] py-[4px] text-sm font-extrabold"
      style={{ background: style.bg, color: style.fg, border: `1px solid ${style.fg}` }}
    >
      {label}
    </span>
  );
}

export function CategoryChip({ label }: { label: string }) {
  return <span className="chip-category">{label}</span>;
}

export function ReviewBadge({
  state,
  days,
}: {
  state: "none" | "due" | "overdue";
  days: number | null;
}) {
  if (state === "none" || days === null) return null;
  const overdue = state === "overdue";
  return (
    <span
      className="pill"
      style={
        overdue
          ? { background: "#FFECEC", color: "#B23438" }
          : { background: "#FCF6D2", color: "#75650B" }
      }
    >
      {overdue
        ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`
        : `Due in ${days} day${days === 1 ? "" : "s"}`}
    </span>
  );
}

// -------------------------------------------------------------------- shell
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  serifSize = "h1",
}: {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  serifSize?: "h1" | "h1home";
}) {
  return (
    <div className="mb-[18px] flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-[240px]">
        {eyebrow && <div className="eyebrow mb-[5px]">{eyebrow}</div>}
        <h1
          className={`m-0 font-display font-normal text-ink ${
            serifSize === "h1home" ? "text-h1home" : "text-h1"
          }`}
        >
          {title}
        </h1>
        {subtitle && <div className="mt-1 text-base text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * A titled panel, optionally foldable.
 *
 * `collapsible` exists because the side rails carry two kinds of panel that
 * deserve different amounts of room. A control the reader acts on — the
 * retrieval scope, the upload category — has to be there. A read-only metric
 * panel is worth a glance a day and costs the same 200px every time. Folding
 * the second kind is what lets the first kind fit on the screen.
 *
 * `storageKey` makes a fold stick. Without it the state would reset on every
 * navigation regardless, because the content pane is keyed on the path and
 * remounts this whole subtree.
 */
export function Card({
  title,
  meta,
  action,
  children,
  className = "",
  padded = true,
  dark = false,
  collapsible = false,
  defaultOpen = true,
  storageKey,
  fill = false,
  info,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  padded?: boolean;
  dark?: boolean;
  /** Fold the body away from the header. Needs a `title` to have a control. */
  collapsible?: boolean;
  /** Open on first sight, before the reader has expressed a preference. */
  defaultOpen?: boolean;
  /** Remember this panel's fold across navigation and reloads. */
  storageKey?: string;
  /** Let the body take the height of a flex parent and scroll inside it. */
  fill?: boolean;
  /**
   * An explainer beside the title.
   *
   * A prop rather than an `InfoDot` passed inside `title`, because on a
   * collapsible card the title *is* the toggle: a button nested in a button is
   * invalid, and the click reached the toggle, so opening the note folded the
   * card away underneath it.
   */
  info?: { title: string; text: string };
}) {
  const bodyId = useId();
  const [open, setOpen] = useState(() =>
    storageKey ? prefs.panelOpen(storageKey, defaultOpen) : defaultOpen,
  );

  const toggle = () =>
    setOpen((current) => {
      if (storageKey) prefs.setPanelOpen(storageKey, !current);
      return !current;
    });

  if (dark) {
    return (
      <section className={`rounded-card bg-ink px-4 py-[15px] text-pearl ${className}`}>
        {title && (
          <div className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-accent">
            {title}
          </div>
        )}
        <div className="text-sm+ leading-[1.65] text-night-50">{children}</div>
      </section>
    );
  }

  const heading =
    typeof title === "string" ? <div className="micro">{title}</div> : title;

  return (
    <section className={`card ${fill ? "flex min-h-0 flex-col" : ""} ${className}`}>
      {(title || action || meta) && (
        <header
          className={`flex items-center gap-3 border-b border-line-soft px-4 py-3 ${
            collapsible ? "" : "justify-between"
          } ${open || !collapsible ? "" : "border-b-0"}`}
        >
          {collapsible ? (
            // Only the title is the control. `action` and `info` stay outside
            // it -- a click on the version selects in "Compare versions" would
            // fold the card it belongs to, and a button nested inside a button
            // is invalid markup besides.
            <>
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              aria-controls={bodyId}
              className="-my-1 -ml-1 flex min-w-0 items-center gap-[7px] rounded-nav py-1 pl-1 pr-1.5 text-left transition-colors hover:bg-surface-subtle"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`shrink-0 text-muted-soft transition-transform duration-150 ${
                  open ? "rotate-90" : ""
                }`}
                aria-hidden
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
              <span className="min-w-0 truncate">{heading}</span>
            </button>
            {info && <InfoDot title={info.title} text={info.text} />}
            {/* The button no longer stretches, so this is what still pushes
                `meta` and `action` to the far end of the row. */}
            <span className="min-w-0 flex-1" aria-hidden />
            </>
          ) : (
            <span className="flex min-w-0 items-center gap-1.5">
              {heading}
              {info && <InfoDot title={info.title} text={info.text} />}
            </span>
          )}
          {meta && <div className="text-sm text-muted-soft">{meta}</div>}
          {action}
        </header>
      )}
      {(open || !collapsible) && (
        <div
          id={bodyId}
          className={`${padded ? "px-4 py-[14px]" : ""} ${
            fill ? "min-h-0 flex-1 overflow-auto" : ""
          }`}
        >
          {children}
        </div>
      )}
    </section>
  );
}

export function Metric({
  label,
  value,
  trend,
  tone = "neutral",
  serif = false,
}: {
  label: string;
  value: string;
  trend?: string;
  tone?: string;
  serif?: boolean;
}) {
  const trendColour =
    tone === "bad" ? "#B23438" : tone === "good" ? "#2F6420" : "#7A737F";
  return (
    <div className="rounded-card border border-line bg-surface px-4 py-[15px]">
      <div className="micro">{label}</div>
      <div
        className={
          serif
            ? "mt-[7px] font-display text-kpiSerif text-ink"
            : "mt-[7px] text-kpi font-extrabold text-ink"
        }
      >
        {value}
      </div>
      {trend && (
        <div className="mt-1 text-sm+ font-bold" style={{ color: trendColour }}>
          {trend}
        </div>
      )}
    </div>
  );
}

export function Bar({
  label,
  value,
  pct,
  colour,
  note,
}: {
  label: string;
  value: string;
  pct: number;
  colour: string;
  note?: string;
}) {
  return (
    <div className="mb-[13px] last:mb-0">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-base+ font-semibold text-ink">{label}</span>
        <span className="text-base+ text-muted">{value}</span>
      </div>
      <div className="h-[5px] w-full overflow-hidden rounded-full bg-surface-rail">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: colour }}
        />
      </div>
      {note && <div className="mt-1 text-sm text-muted-soft">{note}</div>}
    </div>
  );
}

export function Avatar({
  initials,
  colour,
  size = 27,
  title,
}: {
  initials: string;
  colour: string;
  size?: number;
  title?: string;
}) {
  return (
    <span
      title={title}
      className="inline-grid shrink-0 place-items-center rounded-full font-extrabold text-ink"
      style={{
        width: size,
        height: size,
        background: colour,
        fontSize: Math.max(9, Math.round(size * 0.4)),
      }}
    >
      {initials}
    </span>
  );
}

export function Empty({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <div className="text-title font-bold text-ink">{title}</div>
      {detail && <p className="max-w-md text-base+ leading-[1.65] text-muted">{detail}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-base+ text-muted">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-ink" />
      {label ?? "Loading…"}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div
      className="rounded-banner border border-danger-wash bg-danger-wash px-4 py-3 text-base+ text-danger"
      role="alert"
    >
      {message}
    </div>
  );
}

const BANNER_TONE = {
  info: { bg: "#E4F5FB", fg: "#1D5F72", border: "#C5E6F0", dot: "#1D5F72" },
  warn: { bg: "#FCF6D2", fg: "#75650B", border: "#EFE4A8", dot: "#B23438" },
  danger: { bg: "#FFF3E9", fg: "#7A4A21", border: "#F2D9C2", dot: "#B23438" },
  lock: { bg: "#F3EEFA", fg: "#4B2E83", border: "#E3D8F3", dot: "#4B2E83" },
  good: { bg: "#E6F7DC", fg: "#2F6420", border: "#C9E9B8", dot: "#2F6420" },
} as const;

export function Banner({
  tone,
  children,
  action,
}: {
  tone: keyof typeof BANNER_TONE;
  children: ReactNode;
  action?: ReactNode;
}) {
  const palette = BANNER_TONE[tone];
  return (
    <div
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-banner border px-[15px] py-[11px] text-base+"
      style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}
    >
      <div className="flex min-w-0 items-start gap-2">
        <span
          className="mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full"
          style={{ background: palette.dot }}
          aria-hidden
        />
        <div className="min-w-0 leading-[1.6]">{children}</div>
      </div>
      {action}
    </div>
  );
}

/** Underlined tab strip, as used on Document detail, Workflows and Admin. */
export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; count?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div role="tablist" className="mb-4 flex gap-1 border-b border-line">
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`-mb-px border-b-2 px-[13px] py-[9px] text-md transition-colors ${
              isActive
                ? "border-ink font-bold text-ink"
                : "border-transparent font-semibold text-muted hover:text-ink"
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={isActive ? "ml-1.5 opacity-60" : "ml-1.5 text-muted-soft"}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Rounded chip row, as used for saved views, facets and audit filters. */
export function ChipToggle({
  label,
  count,
  active,
  onClick,
  dotColour,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  dotColour?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-[7px] rounded-nav border px-[13px] py-[7px] text-base+ transition-colors ${
        active
          ? "border-ink bg-ink text-pearl"
          : "border-line bg-surface text-muted-strong hover:bg-surface-subtle"
      }`}
    >
      {dotColour && (
        <span
          className="h-[6px] w-[6px] shrink-0 rounded-[2px]"
          style={{ background: dotColour }}
          aria-hidden
        />
      )}
      {label}
      {count !== undefined && (
        <span
          className="text-sm font-extrabold"
          style={{ color: active ? "#8DE971" : "#A49DA8" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/** iOS-style switch: 36×20, knob 16, left 2 → 18, .15s. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative h-5 w-9 shrink-0 rounded-full disabled:opacity-40"
      style={{ background: checked ? "#030304" : "#DDD7DA" }}
    >
      <span
        className="absolute top-[2px] h-4 w-4 rounded-full bg-white transition-[left] duration-150"
        style={{ left: checked ? 18 : 2 }}
      />
    </button>
  );
}

/** Custom checkbox: 15px, radius 4, 1.5px border, green tick on ink. */
export function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="grid h-[15px] w-[15px] shrink-0 place-items-center rounded-[4px] border-[1.5px]"
      style={{
        borderColor: checked ? "#030304" : "#CFC8CD",
        background: checked ? "#030304" : "transparent",
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M20 6 9 17l-5-5"
          stroke={checked ? "#8DE971" : "transparent"}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** The 15px "i" button and its dark popover with a green title. */
/** The width of an `InfoDot` note, which `wouldBeCutOff` has to know. */
const INFO_NOTE_WIDTH = 262;

/**
 * Where to hang the note, as pixels from the trigger's own left edge.
 *
 * Measured against the nearest ancestor that clips rather than against the
 * window: the assistant's rail scrolls, and `overflow-y: auto` forces
 * `overflow-x: auto`, so the note lost its last two words well inside a
 * 1440px viewport.
 *
 * Clamped rather than flipped to the other side. The rail is 355px wide and
 * the card header's dot sits 180px into it, so a note pinned to either edge of
 * a 15px trigger hangs out of one side or the other -- while there is plenty of
 * room for it somewhere in between.
 */
function noteOffset(trigger: HTMLElement | null) {
  if (!trigger) return -8;
  const dot = trigger.getBoundingClientRect();
  let left = 0;
  let right = window.innerWidth;
  for (let el = trigger.parentElement; el; el = el.parentElement) {
    if (getComputedStyle(el).overflowX !== "visible") {
      const box = el.getBoundingClientRect();
      left = box.left;
      right = box.right;
      break;
    }
  }
  // `Math.max` on the upper bound so a clip box narrower than the note itself
  // still yields a bound above the lower one, rather than inverting it.
  const lower = left + 4;
  const upper = Math.max(lower, right - 4 - INFO_NOTE_WIDTH);
  return Math.round(Math.min(Math.max(dot.left - 8, lower), upper) - dot.left);
}

export function InfoDot({ title, text }: { title: string; text: string }) {
  const [open, setOpen] = useState(false);
  // Where the note hangs, decided when it opens rather than on every render:
  // the measurement is a layout read, and the answer cannot change while the
  // note is on screen.
  const [offset, setOffset] = useState(-8);
  const ref = useDismiss(() => setOpen(false));
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <span className="relative inline-flex" ref={ref as never}>
      <button
        ref={trigger}
        type="button"
        aria-label={`About ${title}`}
        onClick={() =>
          setOpen((v) => {
            if (!v) setOffset(noteOffset(trigger.current));
            return !v;
          })
        }
        className="grid h-[15px] w-[15px] place-items-center rounded-full border border-line-strong bg-surface p-0 text-2xs font-extrabold leading-none text-muted hover:border-ink hover:text-ink"
      >
        i
      </button>
      {open && (
        <span
          className="absolute top-5 z-[45] w-[262px] animate-inFast rounded-soft bg-ink px-[13px] py-[11px] text-left normal-case tracking-normal shadow-tip"
          style={{ left: offset }}
        >
          <span className="mb-[5px] block text-sm+ font-extrabold text-accent">
            {title}
          </span>
          <span className="block text-sm+ font-normal leading-[1.6] text-night-25">
            {text}
          </span>
        </span>
      )}
    </span>
  );
}

// ------------------------------------------------------------------- toasts
type Toast = { id: number; text: string; tone: "info" | "danger" };
const ToastContext = createContext<{
  push: (text: string, tone?: Toast["tone"]) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((text: string, tone: Toast["tone"] = "info") => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, text, tone }]);
    // 2600ms, matching the prototype.
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 2600);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-[22px] left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="flex animate-inDrawer items-center gap-2 rounded-banner px-[18px] py-[11px] text-md font-semibold text-pearl shadow-tip"
            style={{ background: toast.tone === "danger" ? "#7A2225" : "#030304" }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: toast.tone === "danger" ? "#FF7276" : "#8DE971" }}
              aria-hidden
            />
            {toast.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context.push;
}

/** Closes a popover on outside click or Escape. */
export function useDismiss(onDismiss: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onDismiss]);
  return ref;
}
